/**
 * Pricing Service
 *
 * Handles pricing calculations based on pricing rules and duration.
 * Supports complex duration types, size-specific pricing, and validity rules.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Json } from '../lib/database.types';

export type DurationType = 'hours' | 'until_closing' | 'calendar_day' | 'multi_day' | 'week' | 'custom';

export interface PricingRule {
  id: string;
  product_type_id: string;
  duration_type: DurationType;
  duration_value: number | null;
  duration_label: string;
  price: number;
  currency: string | null;
  size_specific: boolean | null;
  size: string | null;
  valid_days_of_week: number[] | null;
  valid_time_from: string | null;
  valid_time_until: string | null;
  valid_from: string | null;
  valid_to: string | null;
  display_order: number | null;
  featured: boolean | null;
  active: boolean | null;
  channels: string[];
  created_at: string | null;
  updated_at: string | null;
  location_ids: Json | null;
}

/**
 * The booking_items columns that record which pricing rule (tariff) an item
 * was sold under. They are written together, always from the same rule:
 * readers (the manage-booking app, the booking page) treat pricing_rule_id as
 * the tariff baseline, and a pricing_rule_id that no longer matches the row's
 * duration_* is an error for them, while all-NULL is handled. So when the rule
 * is unknown — e.g. no rule covers a new duration and the previous price is
 * kept — all four are cleared rather than left stale.
 */
export interface ItemPricingRuleColumns {
  pricing_rule_id: string | null;
  duration_type: string | null;
  duration_value: number | null;
  duration_label: string | null;
}

export const NO_PRICING_RULE: ItemPricingRuleColumns = {
  pricing_rule_id: null,
  duration_type: null,
  duration_value: null,
  duration_label: null,
};

export function pricingRuleColumns(rule: PricingRule | null | undefined): ItemPricingRuleColumns {
  if (!rule) return { ...NO_PRICING_RULE };
  return {
    pricing_rule_id: rule.id,
    duration_type: rule.duration_type,
    duration_value: rule.duration_value,
    duration_label: rule.duration_label,
  };
}

/**
 * Fetch pricing rules for a product at a location
 * Filters by location_ids (JSONB contains), active status, and optional size
 *
 * Location filtering logic:
 * - If location_ids is NULL: Rule applies to all locations (include it)
 * - If location_ids is NOT NULL: Check if it contains current locationId
 */
export async function getPricingRules(
  productTypeId: string,
  locationId: string,
  size?: string | null,
  channel?: 'online' | 'admin'
) {
  try {
    console.log(`🔎 Fetching pricing rules for product_type_id: ${productTypeId}`);

    // First, try to fetch ANY rules for this product_type_id to diagnose the issue
    const { data: allRules } = await supabase
      .from('pricing_rules')
      .select('id, active, product_type_id')
      .eq('product_type_id', productTypeId);

    console.log(`🔎 Found ${allRules?.length || 0} total rules (including inactive) for this product_type_id`);
    if (allRules && allRules.length > 0) {
      console.log('🔎 Sample rules:', allRules.slice(0, 3).map(r => ({
        id: r.id.substring(0, 8),
        active: r.active,
        active_type: typeof r.active
      })));
    }

    // Fetch all active rules for this product type
    // We'll do size and location filtering client-side for reliability
    let query = supabase
      .from('pricing_rules')
      .select('*')
      .eq('product_type_id', productTypeId)
      .eq('active', true)
      .order('display_order', { ascending: true });

    // Filter by channel if specified (array containment: channels @> {channel})
    if (channel) {
      query = query.contains('channels', [channel]);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Database query error:', error);
      throw error;
    }

    console.log(`📊 Retrieved ${data?.length || 0} ACTIVE rules from database (before filters)`);

    if (!data || data.length === 0) {
      console.error(`❌ No active pricing rules found for product_type_id: ${productTypeId}`);
      console.error('   Check: Are rules marked as active=true (not NULL, not false)?');
      return { data: [], error: null };
    }

    // Filter by size client-side
    let sizeFilteredData = data.filter(rule => {
      // If size_specific is NULL or false, rule applies to all sizes
      if (!rule.size_specific) {
        return true;
      }

      // If size_specific is true, must match the provided size
      if (rule.size_specific === true) {
        if (!size) {
          // Size-specific rule but no size provided - exclude it
          console.log(`  ✗ Rule ${rule.id.substring(0, 8)}: size_specific=true but no size provided`);
          return false;
        }
        const matches = rule.size === size;
        console.log(`  ${matches ? '✓' : '✗'} Rule ${rule.id.substring(0, 8)}: size ${matches ? 'matches' : 'does not match'} (${rule.size} vs ${size})`);
        return matches;
      }

      return false;
    });

    console.log(`📊 After size filter: ${sizeFilteredData.length} rules remaining`);

    // Filter by location client-side to handle NULL location_ids properly
    // NULL means rule applies to all locations
    const filteredData = sizeFilteredData.filter(rule => {
      // If location_ids is NULL, rule applies to all locations
      if (!rule.location_ids) {
        console.log(`  ✓ Rule ${rule.id.substring(0, 8)}: NULL location_ids (applies to all locations)`);
        return true;
      }

      // If location_ids is an array, check if it contains the locationId
      if (Array.isArray(rule.location_ids)) {
        const matches = rule.location_ids.includes(locationId);
        console.log(`  ${matches ? '✓' : '✗'} Rule ${rule.id.substring(0, 8)}: location_ids array ${matches ? 'contains' : 'does not contain'} ${locationId}`);
        return matches;
      }

      // If location_ids is some other format, exclude it
      console.log(`  ✗ Rule ${rule.id.substring(0, 8)}: location_ids has unexpected format:`, typeof rule.location_ids);
      return false;
    });

    console.log(`📊 After location filter: ${filteredData.length} rules remaining`);

    return { data: filteredData as PricingRule[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Calculate duration in hours based on days and hours
 *
 * IMPORTANT:
 * - "X days with 0 hours" means until end of day (uses calendar_day/multi_day pricing)
 * - "X days Y hours" means exact hours (uses hours pricing)
 * - calendarDays rounds up when extra hours are present because the booking
 *   extends into the next day (e.g. 2 days 3 hours → 3 calendar days). Derived
 *   from the total span (ceil(totalHours / 24)) so it stays correct when the
 *   hours component is itself ≥ 24 (e.g. an exact 48h span → 2 calendar days).
 *   For any hours in [1, 23] this equals the legacy durationDays + 1.
 *
 * @param durationDays - Number of full days
 * @param durationHours - Additional hours beyond days
 * @returns Object with totalHours and isExactHours flag
 */
export function calculateDuration(
  durationDays: number,
  durationHours: number
): { totalHours: number; isExactHours: boolean; calendarDays: number } {
  const isExactHours = durationHours > 0;
  const totalHours = durationDays * 24 + durationHours;
  const calendarDays = isExactHours ? Math.ceil(totalHours / 24) : durationDays;

  return {
    totalHours,
    isExactHours,
    calendarDays
  };
}

/**
 * Check if a pricing rule is valid for the current date/time
 */
function isRuleValid(rule: PricingRule, currentDate: Date): boolean {
  // Check date validity
  if (rule.valid_from) {
    const validFrom = new Date(rule.valid_from);
    if (currentDate < validFrom) return false;
  }

  if (rule.valid_to) {
    const validTo = new Date(rule.valid_to);
    if (currentDate > validTo) return false;
  }

  // Check day of week validity (0 = Sunday, 6 = Saturday)
  if (rule.valid_days_of_week && rule.valid_days_of_week.length > 0) {
    const dayOfWeek = currentDate.getDay();
    if (!rule.valid_days_of_week.includes(dayOfWeek)) return false;
  }

  // TODO: Check time validity (valid_time_from, valid_time_until)
  // This requires comparing time of day, not implemented yet

  return true;
}

/**
 * Select the best pricing rule for the given duration.
 *
 * Considers BOTH hours and calendar-day candidates and returns the cheaper.
 *
 * Algorithm:
 *   1. Hours candidate: smallest 'hours' rule with duration_value >= hoursLookup
 *      (exact match preferred). hoursLookup = elapsedHours when provided,
 *      otherwise durationDays * 24 + durationHours.
 *   2. Calendar candidate: 'until_closing' when calendarDays === 1,
 *      otherwise smallest 'calendar_day' / 'multi_day' rule with
 *      duration_value >= calendarDays (exact match preferred).
 *   3. Pick the cheaper of the two; fall back to whichever exists.
 *
 * Why cheaper-of-both: a "1 calendar day" booking that ends at closing time
 * may span only a few wall-clock hours. With this scheme an hours rule
 * (e.g. 4h) wins over the next-larger calendar rule (e.g. 2 days) when it
 * costs the customer less, instead of silently rounding up.
 *
 * Pass elapsedHours = (end_date - start_date) / 1h whenever the caller has
 * the dates so the hours candidate reflects the actual rental span. When
 * elapsedHours is omitted the legacy totalHours is used.
 */
export function selectPricingRule(
  rules: PricingRule[],
  durationDays: number,
  durationHours: number,
  currentDate: Date = new Date(),
  elapsedHours?: number
): PricingRule | null {
  if (!rules || rules.length === 0) {
    return null;
  }

  const totalHours = durationDays * 24 + durationHours;
  const hoursLookup = elapsedHours ?? totalHours;
  const isExactHours = durationHours > 0;
  // Derived from the total span so it stays correct when the hours component
  // is ≥ 24 (e.g. an exact 48h span → 2 calendar days). Equivalent to the
  // legacy durationDays + 1 for any hours in [1, 23].
  const calendarDays = isExactHours ? Math.ceil(totalHours / 24) : durationDays;

  const validRules = rules.filter(rule => isRuleValid(rule, currentDate));
  if (validRules.length === 0) {
    return null;
  }

  // Hours candidate
  const hoursRules = validRules.filter(r => r.duration_type === 'hours');
  const hoursExact = hoursRules.find(r => r.duration_value === hoursLookup);
  const hoursNextLarger = hoursRules
    .filter(r => r.duration_value !== null && r.duration_value >= hoursLookup)
    .sort((a, b) => (a.duration_value || 0) - (b.duration_value || 0))[0];
  const hoursCandidate: PricingRule | null = hoursExact || hoursNextLarger || null;

  // Calendar candidate (calendar_day / multi_day / until_closing)
  const calendarRules = validRules.filter(r =>
    r.duration_type === 'calendar_day' ||
    r.duration_type === 'multi_day' ||
    r.duration_type === 'until_closing'
  );
  let calendarCandidate: PricingRule | null = null;
  if (calendarDays === 1) {
    calendarCandidate = calendarRules.find(r => r.duration_type === 'until_closing') || null;
  }
  if (!calendarCandidate) {
    const calExact = calendarRules.find(r => r.duration_value === calendarDays);
    const calNextLarger = calendarRules
      .filter(r => r.duration_value !== null && r.duration_value >= calendarDays)
      .sort((a, b) => (a.duration_value || 0) - (b.duration_value || 0))[0];
    calendarCandidate = calExact || calNextLarger || null;
  }

  if (hoursCandidate && calendarCandidate) {
    return hoursCandidate.price <= calendarCandidate.price ? hoursCandidate : calendarCandidate;
  }
  return hoursCandidate || calendarCandidate || null;
}

/**
 * Calculate price for a product based on duration and pricing rules
 *
 * @param productTypeId - Product type ID
 * @param locationId - Location ID
 * @param durationDays - Number of full days
 * @param durationHours - Additional hours
 * @param size - Optional size for size-specific pricing
 * @param channel - Sales channel filter for pricing rules
 * @param elapsedHours - Optional actual wall-clock duration in hours
 *   (end_date - start_date). When provided, lets the hours-rule candidate
 *   reflect the real rental span instead of derived totalHours, which is
 *   what makes calendar-day bookings ending at closing time pick the
 *   cheaper hours rule.
 * @returns Price and selected rule, or null if no rules found
 */
export async function calculatePrice(
  productTypeId: string,
  locationId: string,
  durationDays: number,
  durationHours: number,
  size?: string | null,
  channel?: 'online' | 'admin',
  elapsedHours?: number
): Promise<{
  data: { price: number; rule: PricingRule; durationInfo: ReturnType<typeof calculateDuration> } | null;
  error: string | null;
}> {
  try {
    console.log('🔍 Pricing calculation:', {
      productTypeId,
      locationId,
      durationDays,
      durationHours,
      size
    });

    // Fetch pricing rules
    const { data: rules, error: rulesError } = await getPricingRules(productTypeId, locationId, size, channel);

    if (rulesError) {
      console.error('❌ Error fetching pricing rules:', rulesError);
      return { data: null, error: rulesError };
    }

    console.log(`📋 Found ${rules?.length || 0} pricing rules after location/size filtering`);

    if (!rules || rules.length === 0) {
      console.error('❌ No pricing rules found. Possible reasons:');
      console.error('   - No active pricing rules for this product_type_id');
      console.error('   - Location filter excluded all rules (check location_ids JSONB)');
      console.error('   - Size filter excluded all rules (check size_specific and size fields)');
      return { data: null, error: 'No pricing rules found for this product at this location' };
    }

    // Log available rules for debugging
    console.log('📋 Available rules:', rules.map(r => ({
      id: r.id.substring(0, 8),
      duration_type: r.duration_type,
      duration_value: r.duration_value,
      price: r.price,
      size_specific: r.size_specific,
      size: r.size
    })));

    // Select appropriate pricing rule
    const selectedRule = selectPricingRule(rules, durationDays, durationHours, new Date(), elapsedHours);

    if (!selectedRule) {
      console.error('❌ No suitable pricing rule found for duration');
      console.error(`   Duration: ${durationDays} days, ${durationHours} hours`);
      console.error(`   IsExactHours: ${durationHours > 0}`);
      console.error('   Available rules did not match duration requirements');
      return { data: null, error: 'No suitable pricing rule found for the selected duration' };
    }

    console.log('✅ Selected pricing rule:', {
      id: selectedRule.id.substring(0, 8),
      duration_type: selectedRule.duration_type,
      duration_value: selectedRule.duration_value,
      duration_label: selectedRule.duration_label,
      price: selectedRule.price
    });

    const durationInfo = calculateDuration(durationDays, durationHours);

    return {
      data: {
        price: selectedRule.price,
        rule: selectedRule,
        durationInfo
      },
      error: null
    };
  } catch (error) {
    console.error('❌ Unexpected error in calculatePrice:', error);
    return { data: null, error: handleSupabaseError(error) };
  }
}
